import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { BookNextAvailableResourceApi } from '../../../store/slices/appointmentSlice';
import { normalizeRequirementOptions, resourceAllocationChoices, resourceRequirementProblem } from '../../../helpers/resourceAllocations';
import { buildAppointmentResourceAvailabilityRequest } from './appointmentFormUtils';
import { isSelectedAutofillTimeAvailable } from './appointmentAutofillAvailability';
import { withAvailabilityTimeout, CALENDAR_AVAILABILITY_TIMEOUT_MS } from '../../../utils/availabilityTimeout';
import './AppointmentResourceConflict.css';

const slotKey = (request) => JSON.stringify([
  request.service_id, request.staff_id, request.start_datetime, request.duration,
  request.padding_time, request.resource_item_id, request.resource_allocations,
]);

const availabilityKey = (values) => {
  if (!values) return '';
  const request = buildAppointmentResourceAvailabilityRequest(values);
  return JSON.stringify([Number(request.service_id), Number(request.staff_id), request.start_datetime, moment.duration(request.duration).asMinutes(), moment.duration(request.padding_time || '00:00').asMinutes()]);
};

const AppointmentResourceConflict = ({ formik, response, status, availabilityContext, findNext, onConflictChange, onApply, t = (value) => value }) => {
  const dispatch = useDispatch();
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [acceptedDateKey, setAcceptedDateKey] = useState(null);
  const appliedSlot = useRef(null);
  const cancelLookup = useRef(null);
  const appliedResponse = useRef(null);
  const groups = normalizeRequirementOptions(response);
  const configurationProblem = groups.map(resourceRequirementProblem).filter(Boolean).join(' ');
  const choices = resourceAllocationChoices(formik.values.resource_allocations);
  const unavailable = groups.some((group) => group.options.length === 0 || choices.some((choice) =>
    choice.requirement_id === group.requirement_id && !group.options.some((option) => option.value === choice.resource_item_id)));
  const request = buildAppointmentResourceAvailabilityRequest({
    ...formik.values,
    appointment_id: formik.values.id || '',
    listview: 'Appointment', formtype: 'appointmentForm',
    include_context: false,
  });
  const key = slotKey(request);
  const currentResponse = availabilityContext === undefined || availabilityKey(availabilityContext) === availabilityKey(request);
  const legacyUnknown = Array.isArray(response) && (response.length === 0
    || (request.resource_item_id && !response.some((item) => Number(item.value ?? item.id) === Number(request.resource_item_id))));
  // Manual calendar entry permits staff overlaps and off-roster times.
  // Only actual resource capacity/configuration may prevent saving it.
  const confirmedUnavailable = unavailable || (legacyUnknown && Boolean(formik.values.service_resource_required || formik.values.resource_required));
  const awaitingAppliedAvailability = key === appliedSlot.current && response === appliedResponse.current;
  const conflict = status === 'succeeded' && currentResponse && (unavailable || legacyUnknown)
    && request.service_id && request.staff_id && request.start_datetime;
  const requirementsKey = groups.map((group) => `${group.requirement_id}:${group.unavailable_reason || ''}:${group.options.map((option) => option.value).join(',')}`).join(';');

  useEffect(() => {
    let active = true;
    setResult(null);
    setDismissed(false);
    onConflictChange?.(Boolean(conflict && confirmedUnavailable && !awaitingAppliedAvailability));
    if (!conflict || awaitingAppliedAvailability) return undefined;
    if (configurationProblem) {
      setResult({ key, confirmed: true, configurationProblem });
      return undefined;
    }
    setResult({ key, loading: true, confirmed: confirmedUnavailable });
    const controller = new AbortController();
    // Empty options can reflect a staff overlap, not occupied resources.
    // Recheck the selected slot with the manual booking policy before deciding.
    const lookupRequest = { ...request, validate_manual_slot: true };
    const pending = findNext ? findNext(lookupRequest) : dispatch(BookNextAvailableResourceApi(lookupRequest));
    const cancel = () => {
      if (controller.signal.aborted) return;
      active = false;
      pending.abort?.();
      controller.abort();
    };
    cancelLookup.current = cancel;
    const lookup = findNext ? pending : pending.then((action) => {
      if (action.meta.requestStatus !== 'fulfilled') throw new Error('Availability lookup failed');
      return action.payload;
    });
    // The popup is the only thing a stylist can act on while it is open, so a
    // lookup that never settles must not leave it on the waiting message with
    // no way forward - bound it and fall back to a retryable error.
    Promise.resolve(withAvailabilityTimeout(() => lookup, CALENDAR_AVAILABILITY_TIMEOUT_MS, controller.signal)).then((option) => {
      if (!active) return;
      if (typeof option?.resource_conflict !== 'boolean') throw new Error('Incomplete availability response');
      const confirmed = Boolean(option?.resource_conflict && (confirmedUnavailable || option?.service_assign_resource));
      if (!confirmed) { onConflictChange?.(false); setResult(null); return; }
      onConflictChange?.(true);
      const allocations = resourceAllocationChoices(option?.resource_allocations);
      const complete = groups.every((group) => allocations.some((row) => row.requirement_id === group.requirement_id && row.resource_item_id));
      const requiresResource = groups.length > 0 || Boolean(formik.values.service_resource_required || formik.values.resource_required || option?.service_assign_resource);
      const bookable = option?.staff_id && (!requiresResource || (option?.resource_item_id && complete))
        && isSelectedAutofillTimeAvailable(option.start_datetime, option.time);
      setResult({ key, confirmed: true, option: bookable ? option : null });
    }).catch(() => {
      if (active) setResult({ key, confirmed: confirmedUnavailable, error: true });
    });
    return () => {
      cancel();
      if (cancelLookup.current === cancel) cancelLookup.current = null;
    };
    // Restart only for a changed slot or resource requirement, not a fresh
    // response object or a late staff check confirming the same conflict.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, conflict, requirementsKey, awaitingAppliedAvailability, configurationProblem, retry]);

  if (!conflict || awaitingAppliedAvailability || result?.key !== key || !result.confirmed) return null;
  const suggestedDate = moment(result.option?.start_datetime, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss'], true);
  const differentDate = Boolean(result.option && suggestedDate.isValid()
    && suggestedDate.format('YYYY-MM-DD') !== request.start_datetime.slice(0, 10));
  const dateKey = `${key}:${result.option?.start_datetime}`;
  const dateAccepted = !differentDate || acceptedDateKey === dateKey;
  const apply = () => {
    if (!dateAccepted) return;
    const option = result.option;
    const allocations = resourceAllocationChoices(option.resource_allocations);
    const date = moment(option.start_datetime, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss'], true);
    const fields = { staff_id: option.staff_id, dateof: date.format('dddd, DD MMMM YYYY'),
      start_datetime: option.start_datetime, start_time: date.format('HH:mm'),
      resource_item_id: option.resource_item_id, resource_allocations: allocations };
    // All representations of the selected slot must agree. Later resource
    // changes rebuild start_datetime from dateof/start_time.
    const next = { ...request, ...fields };
    appliedSlot.current = slotKey(next);
    appliedResponse.current = response;
    Object.entries(fields).forEach(([name, value]) => formik.setFieldValue(name, value, false));
    onConflictChange?.(false);
    onApply?.(option, next);
    setResult(null);
  };
  const resourceNames = (result.option?.resource_allocations || [])
    .map((row) => row.resource_item_name || row.resource_item?.resource_item).filter(Boolean);
  if (!resourceNames.length && result.option?.resource_item?.resource_item) resourceNames.push(result.option.resource_item.resource_item);
  const conflictTitle = 'Resource unavailable';
  const message = result.configurationProblem || 'The resource required for this service is not available at this time. The next available time is:';
  const content = <>
    <p className="availability-popup-message h5 mb-3 fw-semibold text-dark">{t(message)}</p>
    {!result.configurationProblem && (result.loading ? <p>{t('Finding the next available staff and resource combination…')}</p>
      : result.error ? <p>{t('Unable to check availability. Please choose another time or try again.')}</p>
      : result.option ? <div className="availability-popup-option btn light-beige w-100 color-wine mb-2" title={resourceNames.join(', ')}><span>{result.option.display_text || `${result.option.start_datetime} — ${t('Staff')}: ${result.option.staff_id}, ${t('Resource')}: ${result.option.resource_item_id}`}</span>
        <strong className="d-block">{suggestedDate.format('dddd, D MMMM YYYY [at] h:mm A')}</strong>
        {resourceNames.length > 1 && <div>{t('Resources')}: {resourceNames.join(', ')}</div>}</div>
      : <p>{t('No staff and resource combination is available. Choose another date or resource.')}</p>)}
    {differentDate && <label className="availability-popup-date-confirm">
      <input type="checkbox" checked={dateAccepted} onChange={(event) => setAcceptedDateKey(event.target.checked ? dateKey : null)} />
      {t('Use this different date')}: {suggestedDate.format('dddd, D MMMM YYYY')}
    </label>}
  </>;
  if (dismissed) return null;
  return createPortal(<div className="modal availability-popup-overlay" role="dialog" aria-modal="true" aria-label={t(conflictTitle)}>
    <div className="modal-content availability-popup">
      <div className="availability-popup-body">{content}</div>
      <div className="availability-popup-footer bg-cream">
        <button type="button" className="availability-popup-cancel btn btn-preview w-100" onClick={() => { cancelLookup.current?.(); setDismissed(true); }}>{t('Cancel')}</button>
        {result.error && <button type="button" className="availability-popup-book btn btn-primary w-100" onClick={() => setRetry((attempt) => attempt + 1)}>{t('Try Again')}</button>}
        {result.option && <button type="button" disabled={!dateAccepted} className="availability-popup-book btn btn-primary w-100" onClick={apply}>{t('Book This Option')}</button>}
      </div>
    </div>
  </div>, document.body);
};

AppointmentResourceConflict.propTypes = {
  formik: PropTypes.shape({values: PropTypes.object.isRequired, setFieldValue: PropTypes.func.isRequired}).isRequired,
  response: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  status: PropTypes.string,
  availabilityContext: PropTypes.object,
  findNext: PropTypes.func,
  onConflictChange: PropTypes.func,
  onApply: PropTypes.func,
  t: PropTypes.func,
};

export default AppointmentResourceConflict;
